import { addDays, format, isBefore, isSameDay } from 'date-fns';
import React, {
  useState,
  useEffect,
  useRef,
  useMemo,
  useCallback
} from 'react';
import { FlatList, Dimensions, Platform } from 'react-native';
import styled from 'styled-components/native';
import Spacer from './Spacer';
import { Feather } from '@expo/vector-icons';
import { B } from './Typography';
import Animated, {
  useAnimatedStyle,
  useSharedValue
} from 'react-native-reanimated';

const ITEM_WIDTH = 54;
const ITEM_MARGIN = 12;

const StyledContainer = styled.View`
  width: 100%;
  padding: 16px 0 0 0;
`;

const StyledTouchable = styled.TouchableOpacity<{ selected: boolean }>`
  margin: 12px 0 0 0;
  background-color: ${p => (p.selected ? '#E6E3FE' : 'white')};
  border-color: ${p => (p.selected ? 'transparent' : '#E6E3FE')};
  border-width: 2px;
  border-radius: 14px;
  width: ${ITEM_WIDTH}px;
  height: 74px;
  align-items: center;
  justify-content: center;
  ${Platform.OS === 'web' ? 'margin-bottom: 6px' : ''}
`;

const StyledDayNameText = styled.Text<{
  selected: boolean;
  missedEvent: boolean;
}>`
  font-family: 'Poppins-Regular';
  font-size: 10px;
  line-height: 15px;
  color: ${p => (p.missedEvent ? '#DC2960' : p.selected ? '#3A2E9E' : 'black')};
`;

const StyledDateText = styled.Text<{
  selected: boolean;
  missedEvent: boolean;
}>`
  font-family: 'Poppins-SemiBold';
  font-size: 16px;
  line-height: 24px;
  color: ${p => (p.missedEvent ? '#DC2960' : p.selected ? '#3A2E9E' : 'black')};
`;

const StyledEventIndicatorContainer = styled.View`
  position: absolute;
  bottom: 1px;
  height: 20px;
  justify-content: center;
`;

const StyledEventIndicator = styled.View<{
  withEvent: boolean;
  missedEvent: boolean;
}>`
  width: 6px;
  height: 6px;
  border-radius: 6px;
  background-color: ${p =>
    p.withEvent ? (p.missedEvent ? '#DC2960' : '#6BD3BA') : 'white'};
  ${p => (!p.withEvent ? 'border-color: #131B3; border-width: 0.5px' : '')}
`;

const StyledSeparator = styled.View`
  width: ${ITEM_MARGIN}px;
`;

const StyledMonthHeader = styled(Animated.View)`
  background-color: white;
  position: absolute;
  left: 0;
  top: 0;
  padding: 0 12px;
`;

export interface CalendarDayScrollEvent {
  date: Date;
  completed?: boolean;
}

interface CalendarDayMonthHeader {
  dateString: string;
  firstIndex: number;
  lastIndex: number;
}

interface CalendarDayScrollEventInternal {
  date: Date;
  withEvents: boolean;
  completed?: boolean;
}

interface CalendarDayScrollProps {
  startDate: Date;
  length: number;
  events?: CalendarDayScrollEvent[];
  selectedDay?: Date;
  onSelect: (date: Date) => void;
}

const CalendarDayScroll: React.FC<CalendarDayScrollProps> = ({
  length,
  events,
  selectedDay,
  onSelect,
  ...props
}) => {
  const flatListRef = useRef<FlatList<CalendarDayScrollEventInternal>>(null!);
  const startDateString = props.startDate.toString();
  const startDate = useMemo(() => new Date(startDateString), [startDateString]);
  const data = useMemo(() => {
    const data = [];
    for (let i = 0; i < length; i++) {
      const date = addDays(startDate, i);
      const dateEvents =
        events?.filter(event => isSameDay(date, event.date)) || [];
      const withEvents = dateEvents.length > 0;
      const completed =
        withEvents && dateEvents.every(event => event.completed);
      data.push({ date, withEvents, completed });
    }
    return data;
  }, [events, length, startDate]);
  const [selected, setSelected] = useState<
    CalendarDayScrollEventInternal | undefined
  >();

  const monthHeaders: Array<CalendarDayMonthHeader> = useMemo(() => {
    const list: Array<CalendarDayMonthHeader> = [];
    const dateStrings = data.map(item => format(item.date, 'MMM yyyy'));
    const uniqueDateStrings = [...new Set(dateStrings)];
    uniqueDateStrings.forEach(u => {
      if (list.length > 0) {
        list.push({
          dateString: u,
          firstIndex: dateStrings.indexOf(u),
          lastIndex: dateStrings.lastIndexOf(u)
        });
      } else {
        list.push({
          dateString: u,
          firstIndex: dateStrings.indexOf(u),
          lastIndex: dateStrings.lastIndexOf(u)
        });
      }
    });
    return list;
  }, [data]);

  const getItemLayout = useCallback((_data: any, index: number) => {
    return {
      length: ITEM_WIDTH + ITEM_MARGIN,
      offset: (ITEM_WIDTH + ITEM_MARGIN) * index,
      index
    };
  }, []);

  const scrollToIndex = useCallback((index: number) => {
    setTimeout(() => {
      flatListRef?.current?.scrollToIndex({
        viewPosition: 0.52,
        index
      });
    }, 100);
  }, []);

  const currentIndex = useMemo(() => {
    const index = data.findIndex(d =>
      isSameDay(d.date, selectedDay || new Date())
    );
    return index;
  }, [data, selectedDay]);

  useEffect(() => {
    if (flatListRef?.current) {
      if (currentIndex >= 0) {
        setSelected(data[currentIndex]);
        scrollToIndex(currentIndex);
      }
    }
  }, [currentIndex, data, scrollToIndex]);

  const [currentMonthText, setCurrentMonthText] = useState<string>(
    monthHeaders[0].dateString
  );
  const [nextMonthText, setNextMonthText] = useState<string>();
  const currentMonthHeaderX = useSharedValue(0);
  const nextMonthHeaderX = useSharedValue(Number.MAX_SAFE_INTEGER);

  const currentMonthHeaderAnimatedStyle = useAnimatedStyle(() => {
    return {
      transform: [{ translateX: currentMonthHeaderX.value }]
    };
  });

  const nextMonthHeaderAnimatedStyle = useAnimatedStyle(() => {
    return {
      transform: [{ translateX: nextMonthHeaderX.value }]
    };
  });

  const handleOnScroll = useCallback(
    (offsetX: number) => {
      const itemSize = ITEM_WIDTH + ITEM_MARGIN;
      for (let i = 0; i < monthHeaders.length; i++) {
        const monthHeaderLastX =
          itemSize * (monthHeaders[i].lastIndex + 1) + ITEM_MARGIN;
        if (monthHeaderLastX > offsetX) {
          const currentHeaderX =
            itemSize * monthHeaders[i].lastIndex - offsetX - ITEM_WIDTH / 2;
          const currrentText = monthHeaders[i].dateString;
          setCurrentMonthText(currrentText);
          if (i < monthHeaders.length - 1) {
            currentMonthHeaderX.value = Math.min(0, currentHeaderX);
            const nextText = monthHeaders[i + 1].dateString;
            setNextMonthText(nextText);
            nextMonthHeaderX.value = Math.max(
              0,
              itemSize * monthHeaders[i + 1].firstIndex - offsetX
            );
          } else {
            if (currentHeaderX < 0) {
              currentMonthHeaderX.value = 0;
            } else {
              currentMonthHeaderX.value = Math.min(0, currentHeaderX);
            }
            nextMonthHeaderX.value = offsetX;
          }
          break;
        }
      }
    },
    [currentMonthHeaderX, monthHeaders, nextMonthHeaderX]
  );
  return (
    <StyledContainer>
      <FlatList
        ref={flatListRef}
        keyExtractor={(_, index) => index.toString()}
        horizontal
        scrollEventThrottle={1}
        decelerationRate={0}
        overScrollMode='never'
        showsHorizontalScrollIndicator={Platform.OS === 'web'}
        snapToInterval={Dimensions.get('window').width / 5}
        data={data}
        getItemLayout={getItemLayout}
        onScrollToIndexFailed={error => {
          scrollToIndex(error.index);
        }}
        initialNumToRender={Math.max(
          10,
          Math.min(currentIndex + 4, data.length)
        )}
        maxToRenderPerBatch={10}
        disableVirtualization={Platform.OS !== 'web'}
        bounces={false}
        onLayout={() => handleOnScroll(0)}
        onScroll={event => {
          const contentOffsetX = event.nativeEvent.contentOffset.x;
          handleOnScroll(contentOffsetX);
        }}
        ItemSeparatorComponent={() => <StyledSeparator />}
        renderItem={listItem => {
          const item = listItem.item;
          const isSelected =
            selected &&
            item.date.toDateString() === selected.date.toDateString();
          const dayName = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
          const past =
            isBefore(item.date, new Date()) &&
            !isSameDay(item.date, new Date());
          const completed = item?.completed;
          const missedEvent = item.withEvents && past && !completed;
          return (
            <StyledTouchable
              selected={Boolean(isSelected)}
              onPress={() => onSelect?.(item.date)}
            >
              <StyledDayNameText
                selected={Boolean(isSelected)}
                missedEvent={missedEvent}
              >
                {dayName[item.date.getDay()]}
              </StyledDayNameText>
              <Spacer size={0.3} />
              <StyledDateText
                selected={Boolean(isSelected)}
                missedEvent={missedEvent}
              >
                {item.date.getDate()}
              </StyledDateText>
              <Spacer size={1} />
              <StyledEventIndicatorContainer>
                {completed ? (
                  <Feather name='check' size={20} color='#1C9C7D' />
                ) : (
                  <StyledEventIndicator
                    withEvent={item.withEvents}
                    missedEvent={missedEvent}
                  />
                )}
              </StyledEventIndicatorContainer>
            </StyledTouchable>
          );
        }}
      />
      <StyledMonthHeader style={[nextMonthHeaderAnimatedStyle]}>
        <B color='dark'>{nextMonthText}</B>
      </StyledMonthHeader>
      <StyledMonthHeader style={[currentMonthHeaderAnimatedStyle]}>
        <B color='dark'>{currentMonthText}</B>
      </StyledMonthHeader>
    </StyledContainer>
  );
};

export default CalendarDayScroll;
